export interface TokenPositions {
  cols: number[];
  rows: number[];
  count: number;
  sessions?: string[];
  pages?: number[];
}

export interface TokenOrigin {
  source:
    | "header"
    | "row_label"
    | "cell_content"
    | "cell_time"
    | "filename"
    | "markdown"
    | "template"
    | "derived";
  raw_source?: string;
  context?: {
    header_section?: "top" | "legend" | "sidebar" | "footer";
    row_label_type?:
      "time_only" | "lesson_number_only" | "lesson_number_and_time";
    row_index?: number;
    lesson_time_slot?: string;
    lesson_number?: number;
    col_index?: number;
    day_header?: string | null;
    time_slot?: string | null;
    page?: number;
    adjacent_tokens?: string[];
  };
  structural?: {
    is_part_of_compound: boolean;
    compound_parent?: string;
    related_subject?: string;
    related_subject_confirmed: boolean;
    appears_in_columns: number[];
    appears_in_rows: number[];
    co_occurs_with: Record<string, number>;
  };
}

export interface TokenMatch {
  token: string;
  field: string;
  confidence: number;
  positions: TokenPositions;
  pattern_source: string | null;
  auto_confirmed: boolean;
  wasPreviouslyConfirmed: boolean;
  teacher_exists?: boolean;
  has_canonical_link?: boolean | null;
  canonical_subject_id?: string | null;
  teacher_id?: string | null;
  room_id?: string | null;
  klass_id?: string | null;
  is_tentative?: boolean;
  origin?: TokenOrigin | null;
  split_into?: string[]; // Parts this token was split into
  is_split?: boolean; // True if this token has been split
  auto_split?: boolean; // Split was automatic; original sits in ignore
}

export interface FieldSuggestion {
  field: string;
  confidence: number;
  reason: string;
}

export interface UnmatchedToken {
  token: string;
  positions: TokenPositions;
  suggestions: FieldSuggestion[];
  split_from: string | null;
  origin?: TokenOrigin | null;
}

export interface CellData {
  col: number;
  row: number;
  content: string;
  day: string | null;
  time: string | null;
  is_multiple_lesson: boolean;
  lesson_size: number;
  confidence: number;
}

export interface MultiLessonGroup {
  col: number;
  rows: number[];
  size: number;
  content: string;
}

export interface TimetableData {
  cells: CellData[];
  headers: string[];
  row_labels: string[];
  multi_lessons: MultiLessonGroup[];
}

export interface ExtractionJobResponse {
  job_id: string;
  status: string;
  message: string;
}

export interface JobStatusResponse {
  job_id: string;
  status: string;
  progress: number;
  message: string;
  pages_done?: number | null;
  pages_total?: number | null;
  result: Record<string, unknown> | null;
  error: string | null;
  started_at: string | null;
  completed_at: string | null;
}

export interface TokenResolution {
  token: string;
  field: string;
  confirmed: boolean;
  origin?: TokenOrigin | null;
  has_canonical_link?: boolean | null;
  canonical_subject_id?: string | null;
  split?: boolean; // True if this token was split into parts
  split_into?: string[]; // The parts this token was split into
  override_ignore?: boolean;
}

export interface ReconstructedEntry {
  teacher_code: string | null;
  day_of_week: string | null;
  time_slot_label: string | null;
  lesson_number: number | null;
  subject: string | null;
  room: string | null;
  class_name: string | null;
  is_multi_period: boolean;
  lesson_count: number;
  multi_period_position: "first" | "middle" | "last" | null;
  raw_content: string;
}

export interface TimetableGridEntry {
  id?: string;
  teacher_code: string | null;
  day_of_week: string | null;
  time_slot_label: string | null;
  lesson_number: number | null;
  subject: string | null;
  subject_id?: string | null;
  subject_token?: string | null;
  subject_unclear?: boolean;
  room: string | null;
  class_name: string | null;
  is_multi_period: boolean;
  lesson_count: number;
  multi_period_position?: "first" | "middle" | "last" | null;
  raw_content?: string;
  is_active?: boolean;
  is_manual_entry?: boolean;
  is_cancelled?: boolean;
  cancelled_reason?: string | null;
  date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  is_clipped?: boolean;
  substitution_excluded?: boolean;
  ampel?: "red" | "yellow" | "green" | "unknown" | null;
  ampel_reasons?: string[];
}

export interface ResolveResponse {
  session_id: string;
  reconstructed_entries: ReconstructedEntry[];
  updated_template_id: string | null;
  error?: string;
  entries_with_invalid_teacher?: boolean;
}

export interface ConflictEntry {
  entry: Record<string, unknown>;
  existing: Record<string, unknown>;
  reason: string;
}

export interface ImportResultResponse {
  entries_imported: number;
  entries_skipped: number;
  conflicts: ConflictEntry[];
}

export interface SessionStatusResponse {
  session_id: string;
  status: string;
  has_unmatched: boolean;
  matched_count: number;
  unmatched_count: number;
  irrelevant_count: number;
  total_tokens: number;
  extraction_strategy: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface TokenEntry {
  token: string;
  field: string | null;
  confidence: number;
  wrong: boolean;
  positions: TokenPositions;
  splitFrom: string | null;
  splitId: string | null;
  ignored: boolean;
  confirmed: boolean;
  confirmedField: string | null;
  source?: string | null;
  pattern?: string | null;
  origin?: TokenOrigin | null;
  originConfirmed?: Record<string, boolean>;
  has_canonical_link?: boolean | null;
  canonical_subject_id?: string | null;
  teacher_exists?: boolean | null;
  is_tentative?: boolean;
  is_split?: boolean; // True if this token has been split
  auto_split?: boolean; // Split was automatic; original sits in ignore
  split_into?: string[]; // Parts this token was split into
  overrideIgnore?: boolean;
}

export interface TokenGroup {
  tokens: string[];
  similarity: number;
}

export interface TokenHistorySnapshot {
  matched: Record<string, TokenEntry>;
  unmatched: Record<string, TokenEntry>;
  wrong: Record<string, TokenEntry>;
  ignored: Record<string, TokenEntry>;
  splits: Record<string, string[]>;
}

export interface TokenAction {
  action: string;
  token?: string;
  tokens?: string[];
  field?: string;
  parts?: string[];
  splitId?: string;
  sources?: string[];
  target?: string;
  prevField?: string;
  newToken?: string;
  combinedText?: string;
  snapshot?: TokenHistorySnapshot;
}

export type TokenTab = "matched" | "wrong" | "unmatched";
export type TokenWorkflowStep = "review" | "fix" | "unmatched" | "save";

export type SelectionResult =
  | { type: "selected"; templateId: string }
  | { type: "skipped" }
  | { type: "cancelled" };

export const DAY_LABELS: Record<string, string> = {
  MON: "Mo",
  TUE: "Di",
  WED: "Mi",
  THU: "Do",
  FRI: "Fr",
  SAT: "Sa",
  SUN: "So",
  __UNASSIGNED_DAY__: "?",
};

export const DAY_ORDER = ["MON", "TUE", "WED", "THU", "FRI", "SAT"];

export interface GridCellData {
  day: string;
  timeSlot: string;
  entries: ReconstructedEntry[];
  colIndex: number;
  rowIndex: number;
  isMultiPeriod: boolean;
  lessonCount: number;
}

export interface GridDimensions {
  days: string[];
  timeSlots: string[];
}

export interface HighlightTarget {
  type: "grid" | "list" | "pdf";
  day?: string;
  timeSlot?: string;
  entryIndex?: number;
  pageNumber?: number;
}
